import { apiClient } from "@nabble/sdk";
import { type MutableRefObject, useCallback } from "react";
import { clearReturnTo, clearSessionExpired } from "./returnTo";
import type { AuthStep } from "./session";

interface LogoutDependencies {
  applyNext: (next: AuthStep) => void;
  refreshUser: () => Promise<void>;
  /** Set while signing out, so the expected 401s don't read as an expired session. */
  loggingOut: MutableRefObject<boolean>;
}

/** Explicit sign out: ends the server session and clears the local state. */
export function useLogout({ applyNext, refreshUser, loggingOut }: LogoutDependencies): () => Promise<void> {
  return useCallback(async () => {
    loggingOut.current = true;
    try {
      await apiClient("/api/auth/logout", { method: "POST" });
    } catch {
      // Best-effort: even if the request fails (e.g. rate-limited), the
      // user asked to leave — clear the client-side session state below.
    }
    clearReturnTo();
    clearSessionExpired();
    applyNext(null);
    await refreshUser();
    loggingOut.current = false;
  }, [applyNext, refreshUser, loggingOut]);
}
